import { useEffect, useState } from 'react';
import type { HostStateView } from '@quiz/shared';
import { api, ApiError } from '../lib/api.js';
import { usePoll } from '../lib/usePoll.js';
import { clearStaff, loadStaff, type StaffSession } from '../lib/session.js';
import { Screen, Spinner, Banner } from '../components/ui.js';
import { StaffLogin } from './StaffLogin.js';
import { HostTab } from './HostTab.js';
import { DoorTab } from './DoorTab.js';
import { PlayersTab } from './PlayersTab.js';

interface StaffEvent {
  id: string;
  name: string;
  entryFeePence: number;
  isFree: boolean;
  isPractice: boolean;
  joinCode: string | null;
}

export function StaffApp() {
  const [staff, setStaff] = useState<StaffSession | null>(loadStaff);
  const [event, setEvent] = useState<StaffEvent | null>(null);
  const [tab, setTab] = useState<'host' | 'door' | 'players'>('host');

  const { state, error, offsetMs, refresh } = usePoll<HostStateView>(
    '/api/host/state',
    staff?.token ?? null,
    2000,
  );

  useEffect(() => {
    if (!staff) return;
    api<{ event: StaffEvent }>('/api/staff/me', { token: staff.token })
      .then((r) => setEvent(r.event))
      .catch((err) => {
        // Only a refusal signs them out. This used to catch everything, so a
        // dropped packet mid-quiz threw the host back to the PIN screen while
        // they were holding a microphone.
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          clearStaff();
          setStaff(null);
        }
      });
  }, [staff]);

  if (!staff) return <StaffLogin onIn={setStaff} />;

  const signOut = () => {
    clearStaff();
    setStaff(null);
  };

  return (
    <Screen>
      <header className="mb-4 flex items-center justify-between">
        <div>
          <p className="font-semibold">{staff.displayName}</p>
          <p className="text-xs text-muted">
            {event?.name ?? '…'}
            {/* So a host never mistakes a practice run for the real night. */}
            {event?.isPractice && <span className="ml-2 text-go">practice</span>}
          </p>
        </div>
        <button onClick={signOut} className="text-sm text-muted underline">
          Sign out
        </button>
      </header>

      {/* Both tabs are role-gated on the server too. Hiding a button is
          presentation, not security. */}
      {staff.role === 'MANAGER' && (
        <div className="mb-4 grid grid-cols-3 gap-2 rounded-xl bg-ink-2 p-1">
          {(['host', 'door', 'players'] as const).map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`min-h-11 rounded-lg text-sm font-semibold capitalize ${
                tab === t ? 'bg-sky text-ink' : 'text-muted'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-3">
          <Banner kind="info">Reconnecting…</Banner>
        </div>
      )}

      {tab === 'players' && staff.role === 'MANAGER' ? (
        <PlayersTab staff={staff} />
      ) : tab === 'door' && staff.role === 'MANAGER' ? (
        event ? (
          <DoorTab staff={staff} event={event} />
        ) : (
          <Spinner />
        )
      ) : state ? (
        <HostTab staff={staff} state={state} offsetMs={offsetMs} refresh={refresh} />
      ) : (
        <Spinner label="Waiting for the event" />
      )}
    </Screen>
  );
}
